/**
 * Shared department Command Center primitives — DeptInbox + KPI tiles +
 * "Route to another department" quick action. Kept lean so every
 * department cockpit (Executive, HR, IT, Finance, Safety, …) can drop
 * these in without duplication.
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Inbox, ArrowRightLeft, CheckCircle2, Clock, AlertTriangle, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminScopeTerms } from '../../lib/adminScopeTerms';
import { useAdminScopeVersion } from '../../lib/adminScopeTransport';

const API = process.env.REACT_APP_BACKEND_URL;

export const DEPT_LIST = [
  { key: 'executive', label: 'Executive Office' },
  { key: 'operations', label: 'Operations' },
  { key: 'finance', label: 'Finance & Accounting' },
  { key: 'hr', label: 'Human Resources' },
  { key: 'it', label: 'Information Technology' },
  { key: 'legal', label: 'Legal & Compliance' },
  { key: 'sales', label: 'Sales & Business Development' },
  { key: 'marketing', label: 'Marketing & Communications' },
  { key: 'customer-service', label: 'Customer Service' },
  { key: 'procurement', label: 'Procurement & Supply Chain' },
  { key: 'safety', label: 'Safety & Risk Management' },
  { key: 'training', label: 'Training & Development' },
];

const PRIORITY_TONE = {
  urgent: 'bg-red-600 text-white',
  high: 'bg-amber-500 text-black',
  normal: 'bg-zinc-600 text-white',
  low: 'bg-zinc-800 text-zinc-300',
};

const STATUS_TONE = {
  open: 'bg-sky-600 text-white',
  in_progress: 'bg-blue-600 text-white',
  resolved: 'bg-emerald-600 text-white',
  cancelled: 'bg-red-700 text-white',
};

/**
 * DeltaChip — Iter-105 · Item 5. Period-over-period percentage change
 * chip. Displayed on every CEO Board KPI card. Green (improvement),
 * red (decline), grey (no change). `positiveIsGood=false` inverts the
 * color logic for metrics where lower is better (e.g. violations).
 */
export function DeltaChip({ current, previous, positiveIsGood = true, testid }) {
  const n = (v) => (typeof v === 'number' && !Number.isNaN(v)) ? v : null;
  const cur = n(current);
  const prev = n(previous);
  if (cur === null || prev === null) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
        style={{ backgroundColor: 'rgba(120,120,120,0.15)', color: '#a1a1aa' }}
        data-testid={testid}
        data-tone="grey"
      >
        —
      </span>
    );
  }
  const rawDelta = cur - prev;
  const pct = prev === 0
    ? (cur === 0 ? 0 : 100)
    : ((rawDelta) / Math.abs(prev)) * 100;
  const rounded = Math.round(pct * 10) / 10;
  const isPositive = rounded > 0;
  const isNegative = rounded < 0;
  let tone = 'grey';
  if (isPositive) tone = positiveIsGood ? 'green' : 'red';
  else if (isNegative) tone = positiveIsGood ? 'red' : 'green';
  const bg = tone === 'green' ? 'rgba(34,197,94,0.18)'
          : tone === 'red'   ? 'rgba(239,68,68,0.18)'
                             : 'rgba(120,120,120,0.15)';
  const fg = tone === 'green' ? '#22c55e'
          : tone === 'red'   ? '#ef4444'
                             : '#a1a1aa';
  const Icon = isPositive ? ArrowUpRight : (isNegative ? ArrowDownRight : Minus);
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full"
      style={{ backgroundColor: bg, color: fg }}
      data-testid={testid}
      data-tone={tone}
    >
      <Icon className="h-3 w-3" />
      {rounded > 0 ? '+' : ''}{rounded}%
    </span>
  );
}

/**
 * KPI card used by every Command Center header row.
 * Iter-105 · Item 5 — accepts `previousValue` + `positiveIsGood` so the
 * period-over-period delta chip is displayed on CEO Board tiles.
 */
export function KpiTile({ icon, label, value, sub, tone = '#C9A962', testid, previousValue, positiveIsGood = true }) {
  const terms = useAdminScopeTerms();
  const hasDelta = previousValue !== undefined && previousValue !== null;
  return (
    <Card
      className="p-4 border-2 min-w-0 break-words"
      style={{ backgroundColor: '#111', borderColor: `${tone}40` }}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] uppercase text-zinc-400 font-semibold" data-testid={`${testid}-label`}>{terms.text(label)}</p>
          <div className="flex items-baseline gap-2 mt-1">
            <p className="text-2xl font-bold" style={{ color: tone, fontFamily: 'Barlow Condensed, sans-serif' }}>
              {value}
            </p>
            {hasDelta && (
              <DeltaChip
                current={typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.-]/g, ''))}
                previous={previousValue}
                positiveIsGood={positiveIsGood}
                testid={testid ? `${testid}-delta` : undefined}
              />
            )}
          </div>
          {sub && <p className="text-[11px] text-zinc-500 mt-0.5">{terms.text(sub)}</p>}
        </div>
        <span className="p-2 rounded-lg" style={{ backgroundColor: `${tone}22`, color: tone }}>{icon}</span>
      </div>
    </Card>
  );
}

/**
 * DeptInbox — shows tasks routed to a specific department with priority
 * chips, status controls, and an inline resolution form.
 */
export function DeptInbox({ deptKey, title = 'DEPARTMENT INBOX' }) {
  const scopeVersion = useAdminScopeVersion();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [routeOpen, setRouteOpen] = useState(false);
  const [resolveOpen, setResolveOpen] = useState({ open: false, task: null, note: '' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/dept-tasks?target_department=${deptKey}`, { credentials: 'include' });
      if (res.ok) setTasks(await res.json());
    } catch (_) { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { setTasks([]); load(); }, [deptKey, scopeVersion]);

  const setStatus = async (task_id, status, note) => {
    try {
      const res = await fetch(`${API}/api/dept-tasks/${task_id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, resolution_note: note || null }),
      });
      if (res.ok) { toast.success(`Marked ${status.replace('_', ' ')}`); load(); }
      else { toast.error('Update failed'); }
    } catch (_) { toast.error('Network error'); }
  };

  return (
    <>
      <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid={`dept-inbox-${deptKey}`}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-base font-bold text-white flex items-center gap-2"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Inbox className="h-5 w-5 text-[#C9A962]" />
            {title}
          </h3>
          <div className="flex items-center gap-2">
            <Badge className="bg-[#C9A962] text-black">{tasks.length}</Badge>
            <Button
              size="sm"
              onClick={() => setRouteOpen(true)}
              className="bg-[#C9A962] hover:bg-[#EAB308] text-black font-bold text-xs"
              data-testid={`dept-route-btn-${deptKey}`}
            >
              <ArrowRightLeft className="h-3 w-3 mr-1" /> Route to another dept
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-xs text-zinc-500 py-6 text-center">Loading…</p>
        ) : tasks.length === 0 ? (
          <p className="text-xs text-zinc-500 py-6 text-center">
            No tasks routed to this department yet. Operational actions that need this team will
            appear here automatically.
          </p>
        ) : (
          <ul className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
            {tasks.map((t) => (
              <li
                key={t.task_id}
                className="bg-zinc-800 border border-zinc-700 rounded-lg p-3"
                data-testid={`dept-task-${t.task_id}`}
              >
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-white">{t.subject}</p>
                    <p className="text-[11px] text-zinc-400 mt-0.5">
                      From {t.created_by_name || 'NECS admin'} · {new Date(t.created_at).toLocaleString()}
                      {t.source_module && ` · ${t.source_module}`}
                    </p>
                    {t.detail && <p className="text-xs text-zinc-300 mt-1 whitespace-pre-wrap">{t.detail}</p>}
                    {t.related_entity && (
                      <p className="text-[11px] text-[#C9A962] mt-1">Ref: {t.related_entity}</p>
                    )}
                    {t.resolution_note && (
                      <p className="text-[11px] text-emerald-400 mt-1">✓ {t.resolution_note}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={PRIORITY_TONE[t.priority] || PRIORITY_TONE.normal}>{t.priority.toUpperCase()}</Badge>
                    <Badge className={STATUS_TONE[t.status] || STATUS_TONE.open}>{(t.status || 'open').replace('_', ' ').toUpperCase()}</Badge>
                  </div>
                </div>
                {(t.status === 'open' || t.status === 'in_progress') && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {t.status === 'open' && (
                      <Button size="sm" variant="outline" className="border-blue-500 text-blue-400 hover:bg-blue-500/10 text-xs h-7"
                              onClick={() => setStatus(t.task_id, 'in_progress')}
                              data-testid={`dept-task-progress-${t.task_id}`}>
                        <Clock className="h-3 w-3 mr-1" /> Working on it
                      </Button>
                    )}
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs h-7"
                            onClick={() => setResolveOpen({ open: true, task: t, note: '' })}
                            data-testid={`dept-task-resolve-${t.task_id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Resolve
                    </Button>
                    <Button size="sm" variant="outline" className="border-red-500 text-red-400 hover:bg-red-500/10 text-xs h-7"
                            onClick={() => setStatus(t.task_id, 'cancelled')}
                            data-testid={`dept-task-cancel-${t.task_id}`}>
                      Cancel
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <RouteTaskDialog open={routeOpen} onClose={() => setRouteOpen(false)} onSaved={load} defaultDept={deptKey} />

      <Dialog open={resolveOpen.open} onOpenChange={(v) => !v && setResolveOpen({ open: false, task: null, note: '' })}>
        <DialogContent className="bg-white text-[#2D2D2D] border border-[#C9A962]/40">
          <DialogHeader>
            <DialogTitle className="text-[#2D2D2D] font-bold">Resolve — {resolveOpen.task?.subject}</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-[#2D2D2D] font-semibold text-sm">Resolution note (optional)</Label>
            <Textarea
              rows={3}
              value={resolveOpen.note}
              onChange={(e) => setResolveOpen((s) => ({ ...s, note: e.target.value }))}
              placeholder="What was done? Attach ticket ID, PR link, or short summary."
              className="mt-1 bg-white text-[#2D2D2D] border-2 border-zinc-300"
              data-testid="dept-resolve-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveOpen({ open: false, task: null, note: '' })}>Cancel</Button>
            <Button
              className="bg-emerald-600 text-white hover:bg-emerald-700 font-bold"
              onClick={async () => {
                await setStatus(resolveOpen.task.task_id, 'resolved', resolveOpen.note);
                setResolveOpen({ open: false, task: null, note: '' });
              }}
              data-testid="dept-resolve-confirm"
            >
              Confirm Resolution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * RouteTaskDialog — dropdown of every NECS department + subject + detail
 * so admins can route work from Operations (or any department) to the
 * appropriate team.
 */
export function RouteTaskDialog({ open, onClose, onSaved, defaultDept = 'hr', prefill }) {
  const [form, setForm] = useState({
    target_department: defaultDept,
    subject: '',
    detail: '',
    priority: 'normal',
    source_module: '',
    related_entity: '',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && prefill) {
      setForm((f) => ({
        ...f,
        target_department: prefill.dept || defaultDept,
        subject: prefill.subject || '',
        detail: prefill.detail || '',
        related_entity: prefill.related || '',
        source_module: prefill.source_module || '',
        priority: prefill.priority || 'normal',
      }));
    } else if (open) {
      setForm((f) => ({ ...f, target_department: defaultDept }));
    }
  }, [defaultDept, open, prefill]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/dept-tasks`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success(`Routed to ${DEPT_LIST.find((d) => d.key === form.target_department)?.label || form.target_department}.`);
        onSaved?.();
        onClose?.();
        setForm({ target_department: defaultDept, subject: '', detail: '', priority: 'normal', source_module: '', related_entity: '' });
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || 'Routing failed');
      }
    } catch (_) { toast.error('Network error'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-white text-[#2D2D2D] border border-[#C9A962]/40 sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-[#2D2D2D] text-xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <ArrowRightLeft className="inline h-5 w-5 mr-2" /> ROUTE WORK TO ANOTHER DEPARTMENT
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 pt-2">
          <div>
            <Label className="text-[#2D2D2D] font-semibold">Target department *</Label>
            <Select value={form.target_department} onValueChange={(v) => setForm((f) => ({ ...f, target_department: v }))}>
              <SelectTrigger className="mt-1 bg-white text-[#2D2D2D] border-2 border-zinc-300" data-testid="route-target-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEPT_LIST.map((d) => <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#2D2D2D] font-semibold">Subject *</Label>
            <Input
              value={form.subject}
              onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              required maxLength={180}
              placeholder="e.g., New hire background check for Alex Rivera"
              className={`mt-1 bg-white text-[#2D2D2D] border-2 ${!form.subject ? 'border-[#C9A962] ring-1 ring-[#C9A962]/40' : 'border-zinc-300'}`}
              data-testid="route-subject-input"
            />
          </div>
          <div>
            <Label className="text-[#2D2D2D] font-semibold">Details</Label>
            <Textarea
              rows={4}
              value={form.detail}
              onChange={(e) => setForm((f) => ({ ...f, detail: e.target.value }))}
              placeholder="Context, deadline, attachments referenced, POC…"
              className={`mt-1 bg-white text-[#2D2D2D] border-2 ${!form.detail ? 'border-[#C9A962] ring-1 ring-[#C9A962]/40' : 'border-zinc-300'}`}
              data-testid="route-detail-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#2D2D2D] font-semibold">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                <SelectTrigger className="mt-1 bg-white text-[#2D2D2D] border-2 border-zinc-300" data-testid="route-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#2D2D2D] font-semibold">Related entity (optional)</Label>
              <Input
                value={form.related_entity}
                onChange={(e) => setForm((f) => ({ ...f, related_entity: e.target.value }))}
                placeholder="Employee, property, vehicle…"
                className="mt-1 bg-white text-[#2D2D2D] border-2 border-zinc-300"
                data-testid="route-entity-input"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={busy || !form.subject}
            className="w-full bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
            data-testid="route-submit-btn"
          >
            {busy ? 'Routing…' : 'Route Task'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function PriorityBanner({ tasks }) {
  const urgent = tasks.filter((t) => t.priority === 'urgent' && t.status !== 'resolved' && t.status !== 'cancelled');
  if (urgent.length === 0) return null;
  return (
    <div className="bg-red-950/50 border-2 border-red-500/60 rounded-lg p-3 flex items-center gap-3">
      <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
      <div>
        <p className="text-sm font-bold text-red-100">{urgent.length} urgent task{urgent.length === 1 ? '' : 's'} awaiting action</p>
        <p className="text-[11px] text-red-200/80">Highest-priority tasks routed to this department.</p>
      </div>
    </div>
  );
}
